import { ApplicationQuoteType, hasValue, Vehicle } from '@amsanalytics/react-shared-library';
import { IApplication } from '../Interfaces/Application/IApplication';
import { IApplicationLeaseQuote } from '../Interfaces/Application/IApplicationLeaseQuote';
import { LeaseCalcMode } from '../Interfaces/Application/ApplicationLeaseCalculationResult';

/** A lease quote with a payment from a Solve that did not fail. */
export const isLeaseQuoteSolved = (quote: IApplicationLeaseQuote | undefined | null): boolean =>
  (quote?.fullPaymentAmount ?? 0) > 0 && !quote?.calcErrorDescription;

/** Values and payoffs of the app's trade-ins, the trade figures a lease Solve uses. */
export const getTradeInTotals = (application: IApplication): { value: number; payoff: number } => {
  const tradeIns = [application.tradeInVehicle, application.tradeInVehicle2].filter((t) => t && !hasValue(t.deletedDate));
  return {
    value: tradeIns.reduce((total, t) => total + (t?.tradeValue ?? 0), 0),
    payoff: tradeIns.reduce((total, t) => total + (t?.tradeBalance ?? 0), 0),
  };
};

export interface LeaseAutoSolve {
  application: IApplication;
  mode: LeaseCalcMode;
}

/**
 * An automatic re-solve repeats the last Solve's mode and Roll Back target (`solvedRollback`, 0 after a
 * Quote / Solve), not a target typed since. Undefined when that Solve is unknown or the quote has none.
 */
export const getLeaseAutoSolve = (application: IApplication, solvedRollback: number | null): LeaseAutoSolve | undefined => {
  if (solvedRollback === null || !isLeaseQuoteSolved(application.applicationLeaseQuote)) {
    return undefined;
  }
  return {
    application: { ...application, applicationLeaseQuote: { ...application.applicationLeaseQuote, totalDownRollback: solvedRollback } },
    mode: solvedRollback > 0 ? 'RollBack' : 'Quote',
  };
};

export interface LeaseVehicleEditEffect {
  /** The quote with the vehicle's new price (the server already set it), when the price changed. */
  quote: IApplicationLeaseQuote | undefined;
  /** The price or ACV a Solve reads changed, so the quote needs a Solve. */
  needsSolve: boolean;
  /** The quote had its own selling price, not the vehicle's, and the server replaced it with the vehicle's price. */
  negotiatedPriceReplaced: boolean;
}

/** What saving an edit of the Lease app's own vehicle does to its lease quote. */
export const getLeaseVehicleEditEffect = (application: IApplication | undefined, vehicle: Vehicle): LeaseVehicleEditEffect => {
  const quote = application?.applicationLeaseQuote;
  if (application?.quoteType !== ApplicationQuoteType.Lease || !quote || application.vehicleId !== vehicle.id) {
    return { quote: undefined, needsSolve: false, negotiatedPriceReplaced: false };
  }
  const priceChanged = hasValue(vehicle.carPrice) && quote.sellingPrice !== vehicle.carPrice;
  const acvChanged = (application.vehicle?.acv ?? 0) !== (vehicle.acv ?? 0);
  return {
    quote: priceChanged ? { ...quote, sellingPrice: vehicle.carPrice } : undefined,
    needsSolve: priceChanged || acvChanged,
    negotiatedPriceReplaced: priceChanged && quote.sellingPrice !== application.vehicle?.carPrice,
  };
};

export type LeaseSendAction = 'desking' | 'posting' | 'printing the amortization schedule';

/**
 * Why a Lease app cannot be desked or posted yet, with the server's texts (LeaseQuoteSolveState.RefusalMessage);
 * undefined when it can or the app is not a lease. Edits and server flags both use the "changed" text: the page
 * cannot tell an input change from a vehicle or trade-in change.
 */
export const getLeaseQuoteRefusal = (
  application: IApplication | undefined, isDirty: boolean, needsSolve: boolean, action: LeaseSendAction,
): string | undefined => {
  if (application?.quoteType !== ApplicationQuoteType.Lease) {
    return undefined;
  }
  const quote = application.applicationLeaseQuote;
  const calculationError = quote?.calcErrorDescription?.trim();
  if (calculationError) {
    return `The last Solve failed: ${calculationError.replace(/\.+$/, '')}. Solve the lease quote again before ${action}.`;
  }
  if (!quote || !((quote.fullPaymentAmount ?? 0) > 0)) {
    return `Solve the lease quote before ${action}.`;
  }
  return isDirty || needsSolve
    ? `The lease quote changed after it was last solved. Solve it again before ${action}.`
    : undefined;
};
